import { useState } from "react";
import toast from "react-hot-toast";

import api from "@/services/api";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    intro: "Get the interview ready for this candidate: the job's common questions, plus the questions their resume leaves open, each with what a good answer sounds like.",
    prepare: "Prepare the interview",
    preparing: "Preparing… about 30 seconds",
    no_resume: "This candidate has no resume, the prep needs one.",
    error: "Failed to prepare the interview",
    rating_error: "Failed to save the rating",
    common_title: "Job questions",
    common_sub: "The same for every candidate to this job, so you can compare them.",
    common_empty: "This job has no common questions yet.",
    own_title: "Questions for {{name}}",
    own_sub: "What the resume leaves open. They complete the job questions.",
    experience: "Past situation",
    situation: "Scenario",
    dig: "To dig into",
    confirm: "To confirm",
    excellent: "Excellent",
    solid: "Solid",
    borderline: "Borderline",
    weak: "Weak",
    your_rating: "Your rating",
    rated: "{{count}} of {{total}} questions rated",
    regenerate: "Regenerate the prep",
    regenerate_confirm: "This replaces the candidate's questions. Ratings on them are lost. Continue?",
  },
  fr: {
    intro: "Préparez l'entretien avec ce candidat : les questions communes de l'offre, plus celles que son CV laisse ouvertes, chacune avec ce qu'est une bonne réponse.",
    prepare: "Préparer l'entretien",
    preparing: "Préparation… environ 30 secondes",
    no_resume: "Ce candidat n'a pas de CV, la préparation en a besoin.",
    error: "Erreur lors de la préparation",
    rating_error: "Erreur lors de l'enregistrement de la note",
    common_title: "Questions de l'offre",
    common_sub: "Les mêmes pour tous les candidats à cette offre, pour pouvoir les comparer.",
    common_empty: "Cette offre n'a pas encore de questions communes.",
    own_title: "Questions pour {{name}}",
    own_sub: "Ce que son CV laisse ouvert. Elles complètent les questions de l'offre.",
    experience: "Situation vécue",
    situation: "Mise en situation",
    dig: "À creuser",
    confirm: "À confirmer",
    excellent: "Excellent",
    solid: "Solide",
    borderline: "Limite",
    weak: "Faible",
    your_rating: "Votre note",
    rated: "{{count}} questions notées sur {{total}}",
    regenerate: "Refaire la préparation",
    regenerate_confirm: "Les questions du candidat seront remplacées, et leurs notes perdues. Continuer ?",
  },
};

const LEVELS = [
  { key: "excellent", text: "text-green-700", active: "bg-green-50 border-green-700 text-green-700" },
  { key: "solid", text: "text-blue-700", active: "bg-blue-50 border-blue-700 text-blue-700" },
  { key: "borderline", text: "text-amber-700", active: "bg-amber-50 border-amber-700 text-amber-700" },
  { key: "weak", text: "text-red-700", active: "bg-red-50 border-red-700 text-red-700" },
];

function Question({ question, kicker, why, rating, onRate, t }) {
  return (
    <div className="mb-8">
      <p className="text-xs font-semibold text-gray-500 mb-1">{kicker}</p>
      {why && <p className="text-sm text-gray-600 mb-2">{why}</p>}
      <p className="border-l-[3px] border-primary pl-4 text-base text-gray-900 mb-3">{question.question}</p>
      <div className="space-y-1">
        {LEVELS.map(({ key, text }) =>
          question.anchors?.[key] ? (
            <p key={key} className="text-sm text-gray-700">
              <span className={`font-semibold ${text}`}>{t(key)} :</span> {question.anchors[key]}
            </p>
          ) : null,
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <span className="text-sm text-gray-500 mr-1">{t("your_rating")}</span>
        {LEVELS.map(({ key, active }) => (
          <button
            key={key}
            type="button"
            aria-pressed={rating === key}
            onClick={() => onRate(question._id, rating === key ? null : key)}
            className={`h-9 px-3 rounded-lg border text-sm font-medium ${rating === key ? active : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"}`}
          >
            {t(key)}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function InterviewPrepTab({ application, setApplication, job, setJob, candidate }) {
  const { t } = useComponentTranslation(translations);
  const [preparing, setPreparing] = useState(false);
  if (!application) return null;

  const prep = application.interview_prep;
  const common = job?.interview_questions || [];
  const own = prep?.questions || [];
  const ratings = Object.fromEntries((application.interview_ratings || []).map((r) => [r.question_id, r.level]));
  const total = common.length + own.length;
  const ratedCount = [...common, ...own].filter((q) => ratings[q._id]).length;

  const handlePrepare = async () => {
    if (prep && !window.confirm(t("regenerate_confirm"))) return;
    setPreparing(true);
    try {
      const { data, job: updatedJob } = await api.post(`/application/${application._id}/interview-prep`);
      setApplication({ ...application, ...data });
      if (updatedJob && setJob) setJob(updatedJob);
    } catch (e) {
      console.error(e);
      toast.error(e?.code === "NO_RESUME" ? t("no_resume") : t("error"));
    } finally {
      setPreparing(false);
    }
  };

  const handleRate = async (question_id, level) => {
    const { ok, data } = await api.put(`/application/${application._id}/interview-rating`, { question_id, level });
    if (!ok) return toast.error(t("rating_error"));
    setApplication({ ...application, ...data });
  };

  if (!prep) {
    return (
      <div className="max-w-2xl mx-auto py-10 text-center">
        <p className="text-base text-gray-700 mb-6">{t("intro")}</p>
        {candidate?.resume_url ? (
          <button type="button" onClick={handlePrepare} disabled={preparing} className="btn btn-primary disabled:opacity-60">
            {preparing ? t("preparing") : t("prepare")}
          </button>
        ) : (
          <p className="text-sm text-gray-500">{t("no_resume")}</p>
        )}
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto text-gray-800">
      {prep.summary && <p className="text-lg leading-relaxed text-gray-900 mb-8">{prep.summary}</p>}

      <h3 className="text-lg font-semibold text-gray-900">{t("common_title")}</h3>
      <p className="text-sm text-gray-500 mb-6">{t("common_sub")}</p>
      {common.length === 0 && <p className="text-sm text-gray-500 mb-8">{t("common_empty")}</p>}
      {common.map((q, i) => (
        <Question
          key={q._id}
          question={q}
          kicker={`${i + 1} · ${t(q.kind)}${q.criterion ? ` · ${q.criterion}` : ""}`}
          rating={ratings[q._id]}
          onRate={handleRate}
          t={t}
        />
      ))}

      {own.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-gray-900 mt-4">{t("own_title", { name: candidate?.firstname || "" })}</h3>
          <p className="text-sm text-gray-500 mb-6">{t("own_sub")}</p>
          {own.map((q) => (
            <Question
              key={q._id}
              question={q}
              kicker={`${t(q.kind)}${q.criterion ? ` · ${q.criterion}` : ""}`}
              why={q.why}
              rating={ratings[q._id]}
              onRate={handleRate}
              t={t}
            />
          ))}
        </>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-gray-100">
        <span className="text-sm text-gray-600">{t("rated", { count: ratedCount, total })}</span>
        <button type="button" onClick={handlePrepare} disabled={preparing} className="text-sm font-medium text-primary disabled:opacity-60">
          {preparing ? t("preparing") : t("regenerate")}
        </button>
      </div>
    </article>
  );
}
